import Box from "@mui/material/Box";
import { useEffect, useRef } from "react";

import { useResolvedScheme } from "../theme";
import { readBridgeMessage } from "./turnstile-bridge";
import {
  loadTurnstileScript,
  renderTurnstile,
  turnstileSiteKey,
} from "./turnstile-script";

/**
 * Cloudflare Turnstile widget.
 *
 * Rendered only when the API reports `captcha_required`, so a build without a site key
 * — local development, CI, the simulator — never loads Cloudflare's script at all.
 *
 * The token is single-use and expires. `expired-callback` clears it so the form cannot
 * submit a stale one, and `error-callback` does the same on a network failure; the API
 * fails closed, so a missing token is rejected rather than waved through.
 *
 * With `bridgeUrl` the widget is not rendered in this document at all: it is solved in
 * a framed page and the token arrives by postMessage. That is how the iOS shell gets
 * one — `turnstile-bridge.ts` explains why it cannot solve the challenge itself.
 */

const SIZE = { width: 300, height: 65 } as const;

export function Turnstile({
  onToken,
  bridgeUrl,
}: {
  onToken: (token: string | null) => void;
  bridgeUrl?: string;
}) {
  return bridgeUrl ? (
    <BridgedWidget bridgeUrl={bridgeUrl} onToken={onToken} />
  ) : (
    <InPageWidget onToken={onToken} />
  );
}

function InPageWidget({
  onToken,
}: {
  onToken: (token: string | null) => void;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const scheme = useResolvedScheme();
  const hasSiteKey = Boolean(turnstileSiteKey());

  useEffect(() => {
    if (!hasSiteKey) return;

    let widgetId: string | undefined;
    let cancelled = false;

    void loadTurnstileScript()
      .then(() => {
        if (cancelled || !containerRef.current) return;
        widgetId = renderTurnstile(containerRef.current, {
          theme: scheme,
          onToken,
        });
      })
      .catch(() => onToken(null));

    return () => {
      cancelled = true;
      if (widgetId && window.turnstile) window.turnstile.remove(widgetId);
    };
  }, [hasSiteKey, onToken, scheme]);

  if (!hasSiteKey) return null;

  return (
    <Box
      ref={containerRef}
      sx={{ display: "flex", justifyContent: "center", minHeight: SIZE.height }}
      data-testid="auth.turnstile"
    />
  );
}

function BridgedWidget({
  bridgeUrl,
  onToken,
}: {
  bridgeUrl: string;
  onToken: (token: string | null) => void;
}) {
  const frameRef = useRef<HTMLIFrameElement>(null);
  const scheme = useResolvedScheme();

  const src = new URL(bridgeUrl);
  src.searchParams.set("theme", scheme);
  const origin = src.origin;

  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      // Only the frame this component rendered speaks for it — not another window that
      // happens to share the origin.
      if (event.origin !== origin) return;
      if (event.source !== frameRef.current?.contentWindow) return;
      const message = readBridgeMessage(event.data);
      if (message) onToken(message.token);
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [origin, onToken]);

  return (
    <Box
      sx={{ display: "flex", justifyContent: "center", minHeight: SIZE.height }}
      data-testid="auth.turnstile"
    >
      <Box
        component="iframe"
        ref={frameRef}
        src={src.toString()}
        title="Cloudflare Turnstile"
        sx={{ ...SIZE, border: "none", colorScheme: scheme }}
      />
    </Box>
  );
}
